import { TabGroup } from '@headlessui/react';
import {
  ArrowUpRightIcon,
  User02Icon,
  UserPlus02Icon,
} from '@langchain/untitled-ui-icons';
import { LinearProgress } from '@mui/joy';

import { useEffect, useMemo, useState } from 'react';
import { match } from 'ts-pattern';

import GraphFillIcon from '@/Pages/Graph/icons/GraphFillIcon.svg?react';
import Breadcrumbs from '@/components/Breadcrumbs';
import { CopyMultiButton } from '@/components/CopyButton/CopyMultiButton';
import { ProfileCircle } from '@/components/ProfileCircle';
import { Skeleton } from '@/components/Skeleton';
import { TabLabel, TabList, TabPanel, TabPanels } from '@/components/Tabs/Tabs';
import { Text } from '@/design-system';
import { useAuth } from '@/hooks/useAuth/useAuth';
import { useColorScheme } from '@/hooks/useColorScheme';
import { OrgConfigs, useOrgConfig } from '@/hooks/useOrgConfig';
import { useStoredResourceTags } from '@/hooks/useStoredResourceTags/useStoredResourceTags';
import {
  useCurrentOrganization,
  useOnboardingState,
  useSelectedWorkspace,
  useUpdateOnboardingStateSuccessViewedAt,
  useWorkspaceStats,
} from '@/hooks/useSwr';
import TraceExpandIcon from '@/icons/TraceExpandIcon.svg?react';
import { TenantsStatsResponse } from '@/types/schema';
import { appOrganizationPath, hostEnabled } from '@/utils/constants';
import { cn } from '@/utils/tailwind';

import { SetupObservability } from '../Onboarding/components/SetupObservability';
import { useNewHomepageGetStartedAssignment } from '../Onboarding/hooks/useNewHomepageGetStartedAssignment';
import { useTraceCount } from '../Settings/PlansAndBillingV2/hooks/useTraceCount';
import { PAYMENT_PLAN_PRICING_MAP } from '../Settings/utils/constants';
import { CreateALangGraphApp } from './CreateALangGraphApp';
import { HomeGetStarted } from './HomeGetStarted';
import { OnboardingChecklistCardRenderer } from './OnboardingChecklistCardRenderer';
import { QuickstartCard } from './QuickstartCard';
import { ResourceSection } from './ResourceSection';
import { WelcomeToLangSmith } from './WelcomeToLangSmith';
import { SECTIONS_MAPING } from './constants';

type HomeSection = 'observability' | 'evaluation' | 'prompts' | 'deployments';
function HomeSection(props: {
  section: HomeSection;
  stats?: TenantsStatsResponse;
}) {
  const { section, stats } = props;
  const [tab, setTab] = useState(0);
  return (
    <div className="flex flex-col gap-3">
      <span className="text-lg font-medium capitalize">
        {SECTIONS_MAPING[section].title}
      </span>
      <TabGroup tabIndex={tab} onChange={setTab}>
        <TabList className="m-0 gap-2 border-none pl-0">
          {SECTIONS_MAPING[section].resources.map(
            ({ type, resourceCountKey, label }) => (
              <TabLabel
                className={cn(
                  'border-none bg-transparent capitalize text-quaternary transition-colors',
                  'rounded-md px-3 py-2 text-sm font-medium',
                  'ui-selected:bg-tertiary ui-selected:text-primary dark:ui-selected:bg-tertiary dark:ui-selected:text-primary',
                  'ui-selected:font-medium',
                  'hover:bg-secondary dark:hover:bg-secondary'
                )}
                key={type}
              >
                {label}
                {stats && (
                  <div className="rounded-full border border-secondary bg-background px-2.5 py-0">
                    {stats[resourceCountKey]}
                  </div>
                )}
              </TabLabel>
            )
          )}
        </TabList>
        <TabPanels className="min-h-[380px]">
          {SECTIONS_MAPING[section].resources.map(
            ({ type, resourceCountKey }) => {
              const isEmpty = stats?.[resourceCountKey] === 0;
              return (
                <TabPanel key={type}>
                  <ResourceSection resourceType={type} isEmpty={isEmpty} />
                </TabPanel>
              );
            }
          )}
        </TabPanels>
      </TabGroup>
    </div>
  );
}

export function Home() {
  // Show the onboarding checklist success UI one time before hiding it on the
  // next page load.
  const [onboardingStateSkip, setOnboardingStateSkip] = useState(false);
  // Don't revalidate while pane is open so checklist doesn't update and
  // trigger updateOnboardingStateSuccessViewedAt.
  const [onboardingStatePause, setOnboardingStatePause] = useState(false);
  const { data: onboardingState } = useOnboardingState({
    skip: onboardingStateSkip || onboardingStatePause,
  });
  const { trigger: updateOnboardingStateSuccessViewedAt } =
    useUpdateOnboardingStateSuccessViewedAt();
  useEffect(() => {
    if (!onboardingState) {
      return;
    }

    if (onboardingState.success_viewed_at) {
      return;
    }

    if (
      onboardingState.tracing_completed_at &&
      onboardingState.lgstudio_completed_at &&
      onboardingState.playground_completed_at
    ) {
      setOnboardingStateSkip(true);
      updateOnboardingStateSuccessViewedAt({});
    }
  }, [onboardingState, updateOnboardingStateSuccessViewedAt]);

  const { isDarkMode } = useColorScheme();

  const { data: workspace, isLoading } = useSelectedWorkspace();
  const { data: organization } = useCurrentOrganization();
  const { total: traceCount } = useTraceCount();

  const auth = useAuth();
  const { selectedTags } = useStoredResourceTags();
  const stats = useWorkspaceStats(
    {
      tag_value_id: selectedTags.map((tag) => tag.tag_value_id),
    },
    {
      keepPreviousData: false,
      skip: !auth?.authed,
    }
  );

  const canUseLangGraphCloud = useOrgConfig(OrgConfigs.can_use_langgraph_cloud);

  // https://github.com/langchain-ai/langchainplus/blob/main/host-backend/host/api/endpoints/projects.py#L45
  const deploymentsEnabled = canUseLangGraphCloud.value;

  const sections = useMemo(() => {
    const result: HomeSection[] = ['observability', 'evaluation', 'prompts'];
    if (deploymentsEnabled && hostEnabled === '1') result.push('deployments');
    return result;
  }, [deploymentsEnabled]);

  const newHomepageGetStartedAssignment = useNewHomepageGetStartedAssignment();
  const showNewHomepageGetStarted =
    newHomepageGetStartedAssignment !== 'control' &&
    onboardingState !== undefined &&
    new Date(onboardingState.created_at).getTime() > 0;

  return (
    <div>
      <div className="border-b border-secondary px-4 py-3">
        <Breadcrumbs />
      </div>

      <div className="flex flex-col gap-9 px-9 py-11 tracking-tight">
        {showNewHomepageGetStarted && !onboardingState.success_viewed_at && (
          <>
            <div className="flex flex-col gap-3">
              <div className="flex">
                <div className="w-[20rem] min-w-[18rem]">
                  <WelcomeToLangSmith onboardingState={onboardingState} />
                </div>

                <div className="flex flex-grow gap-5 overflow-y-scroll">
                  <OnboardingChecklistCardRenderer
                    variant={newHomepageGetStartedAssignment}
                    title={
                      onboardingState.tracing_completed_at
                        ? "You've set up Tracing"
                        : 'Get started with Tracing'
                    }
                    description="Set up tracing with a pre-built ReAct agent"
                    panel={
                      <SetupObservability
                        hideTraceAnApplicationText
                        className="h-full py-0"
                      />
                    }
                    // Keep the panel title the same while trace is loading or
                    // successfully sent.
                    panelTitle="Get started with Tracing"
                    docsLink="https://docs.langchain.com/langsmith/observability-quickstart"
                    onPanelOpen={() => setOnboardingStatePause(true)}
                    onPanelClose={() => setOnboardingStatePause(false)}
                    isCompleted={!!onboardingState.tracing_completed_at}
                    segmentEventButtonName="set_up_tracing"
                    img={
                      <img
                        src={
                          isDarkMode
                            ? '/img/tracing-dark.webp'
                            : '/img/tracing-light.webp'
                        }
                        className="h-full w-full object-contain p-4"
                      />
                    }
                  />

                  <OnboardingChecklistCardRenderer
                    variant={newHomepageGetStartedAssignment}
                    title={
                      onboardingState.lgstudio_completed_at
                        ? "You've successfully run an agent in Studio"
                        : 'Debug and iterate in Studio'
                    }
                    description="Visualize your graph architecture and test in real-time"
                    panel={<CreateALangGraphApp />}
                    docsLink="https://docs.langchain.com/langgraph-platform/local-server"
                    onPanelOpen={() => setOnboardingStatePause(true)}
                    onPanelClose={() => setOnboardingStatePause(false)}
                    isCompleted={!!onboardingState.lgstudio_completed_at}
                    segmentEventButtonName="run_an_agent_in_studio"
                    img={
                      <img
                        src={
                          isDarkMode
                            ? '/img/lgstudio-dark.webp'
                            : '/img/lgstudio-light.webp'
                        }
                        className="h-full w-full object-contain p-6"
                      />
                    }
                  />

                  <OnboardingChecklistCardRenderer
                    variant={newHomepageGetStartedAssignment}
                    title={
                      onboardingState.playground_completed_at
                        ? "You've tried out the Playground"
                        : 'Experiment in the Playground'
                    }
                    description="Test different prompts and models"
                    to={`/${appOrganizationPath}/${workspace?.id}/playground`}
                    isCompleted={!!onboardingState.playground_completed_at}
                    segmentEventButtonName="try_out_playground"
                    img={
                      <img
                        src={
                          isDarkMode
                            ? '/img/playground-dark.webp'
                            : '/img/playground-light.webp'
                        }
                        className="h-full w-full object-contain p-4"
                      />
                    }
                  />
                </div>
              </div>
            </div>

            <hr className="border-secondary" />
          </>
        )}

        {isLoading ? (
          <Skeleton className="m-0 min-h-[36px] w-48" />
        ) : (
          <div className="flex flex-col items-start gap-9 2xl:flex-row 2xl:items-center 2xl:gap-9">
            <div className="flex flex-shrink-0 items-center gap-2">
              <ProfileCircle
                name={
                  workspace?.is_personal
                    ? ''
                    : organization?.display_name ?? 'Organization'
                }
                backupIcon={<User02Icon className="size-4" />}
                className="h-7 w-7"
              />

              <span className="text-3xl">
                {workspace?.is_personal
                  ? 'Personal'
                  : workspace?.display_name ?? 'Workspace'}
              </span>

              <CopyMultiButton
                copyItems={[
                  { prefix: 'Org ID', item: organization?.id ?? '' },
                  { prefix: 'Workspace ID', item: workspace?.id ?? '' },
                ]}
              />
            </div>

            {showNewHomepageGetStarted &&
              onboardingState?.success_viewed_at && (
                <div className="flex flex-grow gap-4">
                  {organization?.tier === 'free' ||
                  organization?.tier === 'developer' ? (
                    <div className="w-[280px] max-w-[400px] 2xl:w-1/3">
                      <QuickstartCard
                        to="/settings/payments"
                        title="Traces"
                        rightContent={
                          <div className="flex flex-grow items-center gap-4">
                            <div className="flex-shrink-0 flex-grow">
                              <LinearProgress
                                value={
                                  (traceCount /
                                    PAYMENT_PLAN_PRICING_MAP[
                                      match(organization.tier)
                                        .with('free', () => 'developer')
                                        .with('developer', () => 'developer')
                                        .exhaustive()
                                    ].numFreeTraces) *
                                  100
                                }
                                determinate
                                color={isDarkMode ? 'neutral' : 'info'}
                                thickness={8}
                              />
                            </div>
                            <div className="flex items-center gap-1">
                              <Text variant="xs" className="font-medium">
                                {traceCount}/
                                {
                                  PAYMENT_PLAN_PRICING_MAP[
                                    match(organization.tier)
                                      .with('free', () => 'developer')
                                      .with('developer', () => 'developer')
                                      .exhaustive()
                                  ].numFreeTraces
                                }
                              </Text>
                              <ArrowUpRightIcon className="size-4" />
                            </div>
                          </div>
                        }
                        segmentEventButtonName="traces_settings_payments"
                      />
                    </div>
                  ) : (
                    <div className="max-w-[400px] 2xl:w-1/3">
                      <QuickstartCard
                        to="/settings/members"
                        title="Invite team"
                        leftContent={
                          <UserPlus02Icon className="size-4 flex-shrink-0 2xl:size-6" />
                        }
                        segmentEventButtonName="invite_your_team"
                      />
                    </div>
                  )}
                  <div className="max-w-[400px] 2xl:w-1/3">
                    <QuickstartCard
                      paneContent={<CreateALangGraphApp />}
                      docsLink="https://docs.langchain.com/langgraph-platform/local-server"
                      title="Studio quickstart"
                      leftContent={
                        <GraphFillIcon className="size-4 flex-shrink-0 2xl:size-6" />
                      }
                      segmentEventButtonName="run_an_agent_in_studio"
                    />
                  </div>
                  <div className="max-w-[400px] 2xl:w-1/3">
                    <QuickstartCard
                      paneContent={
                        <SetupObservability className="h-full py-0" />
                      }
                      docsLink="https://docs.langchain.com/langsmith/observability-quickstart"
                      title="Tracing quickstart"
                      leftContent={
                        <TraceExpandIcon className="size-6 flex-shrink-0 2xl:size-8" />
                      }
                      segmentEventButtonName="set_up_tracing"
                    />
                  </div>
                </div>
              )}
          </div>
        )}

        {!showNewHomepageGetStarted && <HomeGetStarted />}

        {sections.map((section) => (
          <HomeSection section={section} stats={stats.data} key={section} />
        ))}
      </div>
    </div>
  );
}
